import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { UpdateCurrentUserFirstNameLastName } from '@deltek/specification-client/core/GraphQL/Mutations/currentUserInfo.mutations'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { GenerateSharedAccessSignatureQuery } from '@deltek/specification-client/core/GraphQL/Queries/generateSharedAccessSignature.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    GenerateSharedAccessSignatureInput,
    GenerateSharedAccessSignatureResponse,
} from '@deltek/specification-client/core/Models/generateSharedAccessSignature'
import {
    FirmUserResponse,
    User,
} from '@deltek/specification-client/core/Models/user'
import { UserProfile } from '@deltek/specification-client/core/Pages/UserProfileUpdatePage/UserProfile'
import { getNameInitials } from '@deltek/specification-client/core/Utilities/stringUtils'
import { UpdateUserProfile } from 'api/graphQL/mutations/userProfile.mutations'
import { UserProfileQuery } from 'bpm/api/graphQL/queries/userProfile.queries'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useEffect, useState } from 'react'

interface UserProfilePageProps {
    userProfileClicked: boolean
    handleDialogClose?: () => void
}
const UserProfileUpdate: React.FC<UserProfilePageProps> = ({
    userProfileClicked,
    handleDialogClose,
}) => {
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )

    const { data } = useQuery<FirmUserResponse>(UserProfileQuery, {
        variables: {
            firmId:
                currentUserInfoResponse?.currentUserInfo?.account?.accountId ??
                '',
            userId: currentUserInfoResponse?.currentUserInfo?.kpUserId ?? '',
        },
    })
    const [userData, setUserData] = useState<Partial<User>>({} as User)

    const sharedAccessSignatureLazyQuery = useLazyQuery<
        GenerateSharedAccessSignatureResponse,
        GenerateSharedAccessSignatureInput
    >(GenerateSharedAccessSignatureQuery)

    useEffect(() => {
        if (data) {
            setUserData({
                ...data?.user,
                profileInfo: {
                    ...data?.user.profileInfo,
                    firstName:
                        currentUserInfoResponse?.currentUserInfo.firstName,
                    lastName: currentUserInfoResponse?.currentUserInfo.lastName,
                },
            })
        }
    }, [data, currentUserInfoResponse])

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const appClient = useApolloClient()

    const [updateCurrentUserFirstNameLastName] = useMutation<
        CurrentUserInfoResponse
    >(UpdateCurrentUserFirstNameLastName)

    const [updateUserProfile, { loading, error }] = useMutation(
        UpdateUserProfile,
        {
            onCompleted({ updateUserProfile: userProfileData }) {
                appClient.writeQuery({
                    query: UserProfileQuery,
                    data: {
                        user: {
                            ...userData,
                            profileInfo: {
                                ...userProfileData,
                            },
                        },
                    },
                })
                updateCurrentUserFirstNameLastName({
                    variables: {
                        firstName: userProfileData.firstName,
                        lastName: userProfileData.lastName,
                    },
                })
            },
        }
    )

    const userInitials = getNameInitials(
        userData?.profileInfo?.firstName?.toUpperCase(),
        userData?.profileInfo?.lastName?.toUpperCase()
    )

    useEffect(() => {
        if (data) {
            setUserData({
                ...data?.user,
                profileInfo: {
                    ...data?.user.profileInfo,
                    firstName:
                        currentUserInfoResponse?.currentUserInfo.firstName,
                    lastName: currentUserInfoResponse?.currentUserInfo.lastName,
                },
            })
        }
    }, [data, currentUserInfoResponse])

    return !!Object.keys(userData).length ? (
        <UserProfile
            showGlobalSnackbar={showGlobalSnackbar}
            dlogOpen={userProfileClicked}
            userDataInfo={{
                userData: userData,
                currentUserInfo: currentUserInfoResponse?.currentUserInfo,
            }}
            updateUserProfile={updateUserProfile}
            handleDialogClose={handleDialogClose}
            loading={loading}
            error={error}
            userInitials={userInitials}
            apiKey={processEnv.REACT_APP_FileStack_ApiKey}
            kpCoreUri={processEnv.REACT_APP_KPCoreUri}
            sharedAccessSignatureLazyQuery={sharedAccessSignatureLazyQuery}
            blobStorageAccountName={processEnv.REACT_APP_BlobStorageAccountName}
            blobStorageUnverifiedContainerName={
                processEnv.REACT_APP_BlobStorageUnverifiedContainerName
            }
            blobStorageUrl={processEnv.REACT_APP_BlobStorageUrl}
        />
    ) : null
}

export default UserProfileUpdate
